import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CalendarClock, FileWarning } from "lucide-react";
import api from "@/lib/api";
import { fdate } from "@/lib/fmt";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export const useReminders = () => {
  const [data, setData] = useState({ quotations: [], followups: [], total: 0 });
  useEffect(() => {
    const load = () => api.get("/reminders").then((r) => setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, []);
  return data;
};

const qLabel = (q) => (q.expired ? "Sudah expired" : q.days_left < 1 ? "Expired hari ini" : `${Math.ceil(q.days_left)} hari lagi`);
const fLabel = (f) => (f.overdue ? "Terlewat" : f.today ? "Hari ini" : "Segera");

export const ReminderList = ({ data, onGo }) => (
  <div className="space-y-4">
    <div>
      <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-rose-600"><FileWarning className="h-3.5 w-3.5" />Penawaran akan / sudah expired</p>
      {data.quotations.length === 0 && <p className="text-xs text-slate-400">Tidak ada</p>}
      {data.quotations.map((q, i) => (
        <button key={q.id} onClick={() => onGo(`/quotations/${q.id}`)} className="flex w-full items-center justify-between rounded-lg px-2 py-2 text-left hover:bg-slate-50 transition-colors" data-testid={`reminder-quote-${i}`}>
          <span><span className="block font-num text-xs font-semibold text-blue-800">{q.display_no}</span><span className="text-xs text-slate-500">{q.customer_name} • s/d {fdate(q.valid_until)}</span></span>
          <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${q.expired ? "bg-slate-100 text-slate-600" : "bg-rose-50 text-rose-700"}`}>{qLabel(q)}</span>
        </button>
      ))}
    </div>
    <div>
      <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-amber-600"><CalendarClock className="h-3.5 w-3.5" />Jadwal follow up kunjungan</p>
      {data.followups.length === 0 && <p className="text-xs text-slate-400">Tidak ada</p>}
      {data.followups.map((f, i) => (
        <button key={f.id} onClick={() => onGo("/activities")} className="flex w-full items-center justify-between rounded-lg px-2 py-2 text-left hover:bg-slate-50 transition-colors" data-testid={`reminder-followup-${i}`}>
          <span><span className="block text-xs font-semibold">{f.customer_name}</span><span className="text-xs text-slate-500">{fdate(f.next_follow_up)}{f.pic_name ? ` • ${f.pic_name}` : ""}{f.next_action ? ` • ${f.next_action}` : ""}</span></span>
          <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${f.overdue ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700"}`}>{fLabel(f)}</span>
        </button>
      ))}
    </div>
  </div>
);

export const ReminderBell = () => {
  const data = useReminders();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative grid h-9 w-9 place-items-center rounded-full border border-slate-200 bg-white hover:bg-slate-50 transition-colors" data-testid="reminder-bell-btn">
          <Bell className="h-4 w-4 text-slate-600" />
          {data.total > 0 && <span className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-rose-600 px-1 text-[10px] font-bold text-white" data-testid="reminder-count">{data.total}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 max-h-[70vh] overflow-y-auto" data-testid="reminder-popover">
        <p className="font-heading mb-3 font-semibold">Pengingat</p>
        <ReminderList data={data} onGo={(to) => { setOpen(false); nav(to); }} />
      </PopoverContent>
    </Popover>
  );
};
